import {
  Component,
  OnDestroy,
  ChangeDetectionStrategy,
  OnInit,
} from '@angular/core';
import { StateService } from '@shared/services';
import { observableOf, Subscription } from '@shared/rx';
import { get } from 'lodash';
import { IFieldPreferences } from '@components/form/services';
import { IStandWorksheetSummaryFilter } from '@shared/types/common/stand-worksheet-summary.type';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: '',
})
export abstract class DashboardWidgetBase implements OnInit, OnDestroy {
  protected field: IFieldPreferences;
  private intervalFetchId: NodeJS.Timeout = null;
  public subscriptions: Subscription[] = [];
  public widgetQueryFilter: IStandWorksheetSummaryFilter;

  protected constructor(protected stateService: StateService) {}

  public ngOnInit(): void {
    const autoRefresh$ = this.field.widgetData?.autoRefresh?.autoRefreshObs
      ? this.field.widgetData.autoRefresh.autoRefreshObs
      : observableOf(false);
    const autoRefreshSub = autoRefresh$.subscribe(
      async (autoRefreshEvent: boolean) => {
        const autoRefreshToggle = this.stateService.getAutoRefreshToggle(
          this.field.path,
        );
        if (autoRefreshEvent && autoRefreshToggle && !this.intervalFetchId) {
          this.scheduleNextDataUpdate(
            get(this.field, ['widgetData', 'autoRefresh', 'interval'], 30) *
              1000,
          );
        } else if (!autoRefreshEvent && this.intervalFetchId) {
          clearInterval(this.intervalFetchId);
          this.intervalFetchId = null;
        }
      },
    );
    this.subscriptions.push(autoRefreshSub);
  }

  public abstract handleDataUpdate(
    _widgetQueryFilter: IStandWorksheetSummaryFilter,
  ): Promise<void>;

  private scheduleNextDataUpdate(refreshInterval: number): void {
    this.intervalFetchId = setTimeout(() => {
      this.handleDataUpdate(this.widgetQueryFilter).finally(() =>
        this.scheduleNextDataUpdate(refreshInterval),
      );
    }, refreshInterval);
  }

  public ngOnDestroy(): void {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
    clearInterval(this.intervalFetchId);
    this.intervalFetchId = null;
  }

  public stopFetchInterval(): void {
    this.widgetQueryFilter = null;
    clearInterval(this.intervalFetchId);
    this.intervalFetchId = null;
  }
}
